/**
 * Fila de toques ainda não enviados (Feito, Voltar, Pular, Pausar, Cancelar).
 *
 * Todo toque entra aqui antes de ir ao servidor — online ou não — e só sai
 * quando o servidor responde. Como cada evento leva um id gerado no aparelho
 * e o backend ignora id repetido, reenviar depois de uma queda nunca duplica
 * (ADR-0087, decisão 6). A ordem de envio não importa: o estado é a projeção.
 *
 * Sem dependência de React nem de rede: `armazem` e `enviar` são injetados
 * (o teste usa o armazém em memória e um `enviar` falso).
 */
import { CHAVE_FILA } from "./armazem";

/** Recuo entre tentativas quando a rede falha: 5 s, 10 s, 20 s, 40 s, 60 s… */
export function proximoIntervalo(tentativas) {
  return Math.min(60000, 5000 * 2 ** Math.max(0, tentativas));
}

export function criarFila({ armazem, enviar, ehFalhaDeRede }) {
  let enviando = null;

  async function itens() {
    return (await armazem.ler(CHAVE_FILA)) || [];
  }

  async function acrescentar(execucaoId, eventos) {
    const atuais = await itens();
    const novos = eventos.map((evento) => ({ execucaoId, evento }));
    await armazem.gravar(CHAVE_FILA, [...atuais, ...novos]);
    return atuais.length + novos.length;
  }

  async function tirar(ids) {
    const atuais = await itens();
    const restantes = atuais.filter((item) => !ids.has(item.evento.id));
    await armazem.gravar(CHAVE_FILA, restantes);
    return restantes;
  }

  /** `{execucaoId: [evento]}` do que ainda não saiu. */
  async function pendentesPorExecucao() {
    return (await itens()).reduce((mapa, { execucaoId, evento }) => {
      (mapa[execucaoId] = mapa[execucaoId] || []).push(evento);
      return mapa;
    }, {});
  }

  /**
   * Envia tudo, uma execução por vez. Para na primeira falha de REDE (o resto
   * fica para a próxima tentativa); erro de regra (4xx) descarta o lote e
   * volta em `recusados` para a tela avisar.
   */
  async function enviarTudo() {
    const resultado = { respostas: [], recusados: [], semRede: false, restantes: 0 };
    const grupos = await pendentesPorExecucao();
    for (const [execucaoId, eventos] of Object.entries(grupos)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const resposta = await enviar(execucaoId, eventos);
        resultado.respostas.push({ execucaoId, resposta });
      } catch (erro) {
        if (ehFalhaDeRede(erro)) {
          resultado.semRede = true;
          break;
        }
        resultado.recusados.push({ execucaoId, erro, eventos });
      }
      // eslint-disable-next-line no-await-in-loop
      await tirar(new Set(eventos.map((e) => e.id)));
    }
    resultado.restantes = (await itens()).length;
    return resultado;
  }

  /**
   * Um envio por vez. Toque que chega DURANTE um envio não entrou na leitura
   * dele: a chamada marca outra rodada, que sai assim que a atual termina (sem
   * isso o toque ficava "pendente" até a próxima ação).
   */
  let deNovo = false;
  function sincronizar() {
    if (enviando) {
      deNovo = true;
      return enviando;
    }
    enviando = (async () => {
      let resultado;
      do {
        deNovo = false;
        // eslint-disable-next-line no-await-in-loop
        const rodada = await enviarTudo();
        resultado = resultado
          ? { ...rodada, respostas: [...resultado.respostas, ...rodada.respostas], recusados: [...resultado.recusados, ...rodada.recusados] }
          : rodada;
      } while (deNovo && !resultado.semRede);
      return resultado;
    })().finally(() => {
      enviando = null;
    });
    return enviando;
  }

  return { acrescentar, pendentesPorExecucao, sincronizar, quantidade: async () => (await itens()).length };
}
